/**
 * Module Types
 *
 * Container format for distributable Machina content packs.
 * A module packages sessions, bestiary, maps, NPCs, factions, lore,
 * and playsets into a single unit that can be validated, shared, and installed.
 *
 * Special module type: 'game-system' provides a rules engine (abilities,
 * classes, combat, magic) that other modules target.
 */
import { z } from 'zod';
// =============================================================================
// MODULE IDENTITY
// =============================================================================
export const ModuleAuthorSchema = z.object({
    name: z.string(),
    url: z.string().url().optional(),
    avatar: z.string().optional(),
});
export const ModuleLicenseSchema = z.enum([
    'cc-by-4.0',
    'cc-by-sa-4.0',
    'cc-by-nc-4.0',
    'cc-by-3.0',
    'ogl-1.0a',
    'orc-1.0',
    'custom',
]);
export const ModuleTypeSchema = z.enum([
    'one-shot',
    'adventure',
    'campaign',
    'supplement',
    'world',
    'bestiary-pack',
    'map-pack',
    'game-system',
]);
// =============================================================================
// MODULE ASSETS
// =============================================================================
export const AssetTypeSchema = z.enum([
    'image',
    'token',
    'map',
    'audio',
    'video',
    'font',
    'other',
]);
export const ModuleAssetSchema = z.object({
    id: z.string(),
    type: AssetTypeSchema,
    path: z.string(), // relative path within module
    contentHash: z.string().optional(), // SHA-256 for content-addressing
    mimeType: z.string().optional(),
    sizeBytes: z.number().int().optional(),
});
// =============================================================================
// MODULE DEPENDENCIES
// =============================================================================
export const ModuleDependencySchema = z.object({
    moduleId: z.string(),
    versionRange: z.string().default('*'), // semver range
    optional: z.boolean().default(false),
});
// =============================================================================
// EXTENSION POINTS
// =============================================================================
/** Definition for a custom AI DM tool that a module provides */
export const ToolDefinitionSchema = z.object({
    name: z.string(),
    description: z.string(),
    parameters: z.record(z.string(), z.unknown()).optional(),
    handler: z.string().optional(), // path to handler function (future bundled modules)
    handlerBody: z.string().optional(), // inline JS function body (executed with ModuleAPI)
});
/** Definition for a custom /slash command */
export const CommandDefinitionSchema = z.object({
    name: z.string(), // e.g. "weather" for /weather
    description: z.string(),
    usage: z.string().optional(), // e.g. "/weather <location>"
    handler: z.string().optional(), // path to handler function (future bundled modules)
    handlerBody: z.string().optional(), // inline JS function body — receives (api, ctx), returns string
});
/** Definition for a game event handler */
export const EventHandlerDefSchema = z.object({
    event: z.string(), // e.g. "combat_start", "scene_change", "npc_death"
    handler: z.string().optional(), // path to handler function (future bundled modules)
    handlerBody: z.string().optional(), // inline JS function body — receives (api, payload), void
    priority: z.number().int().default(0), // higher = runs first
    description: z.string().optional(),
});
// =============================================================================
// GAME SYSTEM — RULES DEFINITIONS
// =============================================================================
/** A single ability score definition (system-generic) */
export const AbilityDefinitionSchema = z.object({
    id: z.string(), // "str", "dexterity", "cool", "sharp"
    name: z.string(), // "Strength", "Cool"
    abbreviation: z.string(), // "STR", "COL"
    description: z.string().optional(),
});
/** A condition/status effect definition */
export const ConditionDefinitionSchema = z.object({
    id: z.string(), // "blinded", "frightened", "marked"
    name: z.string(), // "Blinded", "Frightened"
    description: z.string(),
    hasLevels: z.boolean().default(false), // e.g. exhaustion
    maxLevel: z.number().int().optional(),
});
/** A character class definition (for class-based systems) */
export const ClassDefinitionSchema = z.object({
    id: z.string(), // "fighter", "wizard", "gunslinger"
    name: z.string(), // "Fighter", "Wizard"
    hitDie: z.number().int().optional(), // d6, d8, d10, d12
    primaryAbility: z.array(z.string()).optional(), // ability IDs
    savingThrows: z.array(z.string()).optional(),
    subclassLevel: z.number().int().optional(),
    spellcasting: z.enum(['full', 'half', 'third', 'pact', 'none']).optional(),
    description: z.string().optional(),
});
/** Core dice resolution mechanic */
export const CoreResolutionSchema = z.object({
    type: z.enum([
        'd20-roll-high', // D&D, PF, 13th Age
        '2d6-pbta', // Powered by the Apocalypse
        'dice-pool', // Shadowrun, World of Darkness
        'fate-dice', // Fate Core (4dF)
        'percentile', // Call of Cthulhu, WFRP
        'd100', // BRP
        'd20-roll-under', // OSR variants, Cairn
        'step-dice', // Savage Worlds
        'custom',
    ]),
    critRange: z.array(z.number().int()).optional(), // [20] for 5e, [19,20] for improved crit
    fumbleRange: z.array(z.number().int()).optional(), // [1] for 5e
    degreesOfSuccess: z.number().int().optional(), // 4 for PF2e
});
/** Action types available in combat */
export const ActionTypeSchema = z.object({
    id: z.string(), // "action", "bonus_action", "reaction", "free"
    name: z.string(),
    description: z.string().optional(),
    countPerRound: z.number().int().default(1),
});
/** Combat framework definition */
export const CombatFrameworkSchema = z.object({
    initiativeFormula: z.string().optional(), // "1d20 + dex_mod" for 5e
    actionEconomy: z.array(ActionTypeSchema).optional(),
    roundDuration: z.string().optional(), // "6 seconds" for 5e
    gridBased: z.boolean().default(true),
    gridUnit: z.string().default('5ft'), // "5ft", "1 zone", "1 square"
    deathMechanic: z.string().optional(), // "death-saves", "wound-levels", etc.
});
/** Magic / powers system definition */
export const MagicSystemSchema = z.object({
    type: z.enum([
        'spell-slots', // D&D 5e, PF2e
        'mana', // point-buy casting
        'power-points', // psionics, Cypher
        'narrative', // Fate, PbtA
        'vancian', // OSR
        'none',
        'custom',
    ]),
    castingAbilities: z.array(z.string()).optional(),
    concentrationRule: z.boolean().default(false),
    description: z.string().optional(),
});
/** Encounter balance system */
export const EncounterBalanceSchema = z.object({
    ratingSystem: z.enum(['cr', 'level', 'threat', 'none']),
    description: z.string().optional(),
});
/** Character template — what a character looks like in this system */
export const CharacterTemplateSchema = z.object({
    levelCap: z.number().int().optional(), // 20 for 5e, null for classless
    usesClasses: z.boolean().default(true),
    usesRaces: z.boolean().default(true),
    progressionType: z.enum(['xp', 'milestone', 'advancement-track', 'freeform']).default('xp'),
    xpTable: z.array(z.number().int()).optional(),
    healthModel: z.enum([
        'hit-points', // D&D, PF
        'stress-track', // Fate
        'wound-levels', // Savage Worlds, MÖRK BORG
        'fate-stress', // Fate-specific
        'custom',
    ]).default('hit-points'),
    defenseModel: z.enum([
        'armor-class', // D&D, PF
        'defense-rating', // Some OSR
        'dodge-parry', // RuneQuest
        'static', // Fate
        'custom',
    ]).default('armor-class'),
});
/** AI DM guidance — helps the AI understand the system's idioms */
export const DmGuidanceSchema = z.object({
    systemBrief: z.string(), // 2-3 paragraph overview
    combatNorms: z.string(), // What "normal" combat looks like
    socialNorms: z.string(), // How social encounters work
    toneGuidance: z.string(), // System's intended play feel
    commonMistakes: z.array(z.string()), // "Don't use spell slots — this system uses mana"
});
/** Complete rules definition for a game system */
export const GameSystemRulesSchema = z.object({
    // Core identity
    name: z.string(),
    shortName: z.string(),
    license: z.string(),
    publisher: z.string().optional(),
    /**
     * What this system calls the person running the game, table-facing.
     *
     * "Dungeon Master" is a D&D term and reads wrong everywhere else. Doomsworn
     * uses "the Narrator", which is already the language of its own design docs
     * (02-NARRATOR-CONTRACT.md) and carries the authority split: the Narrator
     * describes, and the engine remembers. Defaults to Game Master so a system
     * that does not care needs no opinion.
     */
    gamemasterTitle: z.string().optional(),
    // Ability framework
    abilities: z.array(AbilityDefinitionSchema),
    abilityModFormula: z.string().optional(), // "(score - 10) / 2" for d20 systems
    // Character structure
    characterTemplate: CharacterTemplateSchema,
    classes: z.array(ClassDefinitionSchema).optional(),
    // Dice & resolution
    coreResolution: CoreResolutionSchema,
    // Combat (optional — narrative systems may skip)
    combat: CombatFrameworkSchema.optional(),
    // Conditions / status effects
    conditions: z.array(ConditionDefinitionSchema).optional(),
    // Magic / powers (optional)
    magic: MagicSystemSchema.optional(),
    // Encounter balance (optional)
    encounterBalance: EncounterBalanceSchema.optional(),
    // AI DM hints
    dmGuidance: DmGuidanceSchema.optional(),
});
// =============================================================================
// MACHINA MODULE — THE CONTAINER
// =============================================================================
/**
 * MachinaModule is the top-level container for distributable content.
 *
 * Everything except identity fields is optional — a module can be
 * a bestiary pack, a map pack, a full campaign, or a game system.
 * Compose what you need.
 */
export const MachinaModuleSchema = z.object({
    // --- Identity (required) ---
    id: z.string(), // unique slug
    version: z.string().default('1.0.0'), // semver
    name: z.string(), // display name
    description: z.string(), // 1-3 sentences
    author: ModuleAuthorSchema,
    license: ModuleLicenseSchema.default('cc-by-4.0'),
    // --- Classification ---
    type: ModuleTypeSchema,
    tags: z.array(z.string()).default([]),
    system: z.string().default('system-agnostic'), // system module ID
    levelRange: z.tuple([z.number().int(), z.number().int()]).optional(),
    playerCount: z.tuple([z.number().int(), z.number().int()]).optional(),
    estimatedDuration: z.string().optional(),
    // --- Content (all optional — compose what you need) ---
    // These reference the existing core types by structure, not by import,
    // to avoid circular deps. Module validation checks against the active system.
    sessions: z.array(z.record(z.string(), z.unknown())).optional(),
    bestiary: z.array(z.record(z.string(), z.unknown())).optional(),
    maps: z.array(z.record(z.string(), z.unknown())).optional(),
    npcs: z.array(z.record(z.string(), z.unknown())).optional(),
    factions: z.array(z.record(z.string(), z.unknown())).optional(),
    districts: z.array(z.record(z.string(), z.unknown())).optional(),
    scenarios: z.array(z.record(z.string(), z.unknown())).optional(),
    questHooks: z.array(z.record(z.string(), z.unknown())).optional(),
    journalEntries: z.array(z.record(z.string(), z.unknown())).optional(),
    // --- Game System (only for type: 'game-system') ---
    rules: GameSystemRulesSchema.optional(),
    extends: z.string().optional(), // parent system module ID (for forks)
    // --- Extension Points ---
    customTools: z.array(ToolDefinitionSchema).optional(),
    customCommands: z.array(CommandDefinitionSchema).optional(),
    eventHandlers: z.array(EventHandlerDefSchema).optional(),
    // --- Assets ---
    assets: z.array(ModuleAssetSchema).optional(),
    thumbnail: z.string().optional(),
    // --- Dependencies ---
    requires: z.array(ModuleDependencySchema).optional(),
    compatibleWith: z.string().optional(), // minimum Machina version
});
// =============================================================================
// VALIDATION HELPERS
// =============================================================================
/** Validate that a game-system module has rules defined */
export function validateGameSystemModule(mod) {
    const errors = [];
    if (mod.type === 'game-system') {
        if (!mod.rules) {
            errors.push('Game system modules must define "rules"');
        }
        if (mod.rules && mod.rules.abilities.length === 0 && !mod.extends) {
            errors.push('Game system must define at least one ability (or extend a parent system)');
        }
    }
    if (mod.extends && mod.type !== 'game-system') {
        errors.push('"extends" is only valid for game-system modules');
    }
    return errors;
}
/** Validate basic module structure */
export function validateModule(mod) {
    const parsed = MachinaModuleSchema.safeParse(mod);
    if (!parsed.success) {
        return {
            success: false,
            errors: parsed.error.issues.map(i => `${i.path.join('.')}: ${i.message}`),
        };
    }
    const systemErrors = validateGameSystemModule(parsed.data);
    if (systemErrors.length > 0) {
        return { success: false, errors: systemErrors };
    }
    return { success: true, data: parsed.data };
}
//# sourceMappingURL=module-types.js.map